import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

interface PricingBreakdown {
  base_package_monthly: number;
  base_package_yearly?: number;
  base_package_setup?: number;
  toolkits_monthly: number;
  toolkits_yearly?: number;
  toolkits_setup?: number;
  custom_services_monthly: number;
  custom_services_setup?: number;
  physical_products_total: number;
  shipping_total: number;
  resource_upgrades_monthly: number;
  total_monthly: number;
  total_yearly?: number;
  total_setup: number;
  total_one_time: number;
}

interface QuoteItem {
  toolkit_id?: string;
  service_id?: string;
  product_id?: string;
  name: string;
  description: string;
  monthly_price?: number;
  yearly_price?: number;
  price?: number;
  category?: string;
}

interface Quote {
  quote_id: string;
  base_package: {
    id: string;
    name: string;
    description: string;
    included_services: string[];
    base_resources: Record<string, any>;
  };
  toolkit_addons: QuoteItem[];
  custom_services: QuoteItem[];
  physical_products: QuoteItem[];
  pricing: PricingBreakdown;
  estimated_deployment_time: string;
  support_level: string;
  valid_until: string;
}

export const QuoteDetailPage: React.FC = () => {
  const { quoteId } = useParams<{ quoteId: string }>();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchQuote = async () => {
      if (!quoteId || !token) {
        setError('Quote ID or authentication token missing');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/package-builder/quotes/${quoteId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 404) {
            setError('Quote not found');
          } else if (response.status === 401) {
            setError('Unauthorized access');
            navigate('/login');
            return;
          } else {
            setError('Failed to load quote');
          }
          setLoading(false);
          return;
        }

        const quoteData = await response.json();
        setQuote(quoteData);
      } catch (err) {
        console.error('Error fetching quote:', err);
        setError('Failed to load quote');
      } finally {
        setLoading(false);
      }
    };

    fetchQuote();
  }, [quoteId, token, navigate]);

  const handleConvertToOrder = async () => {
    if (!quote || !token) return;

    try {
      const response = await fetch(`/api/package-builder/quotes/${quote.quote_id}/order`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const orderData = await response.json();
        navigate(`/orders/${orderData.order_id}`);
      } else {
        setError('Failed to convert quote to order');
      }
    } catch (err) {
      console.error('Error converting quote:', err);
      setError('Failed to convert quote to order');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const isQuoteValid = (validUntil: string) => {
    return new Date(validUntil) > new Date();
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-red-800 mb-2">Error</h2>
          <p className="text-red-600">{error || 'Quote not found'}</p>
          <Link
            to="/packages"
            className="inline-block mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Back to Packages
          </Link>
        </div>
      </div>
    );
  }

  const validUntil = quote.valid_until;
  const isValid = isQuoteValid(validUntil);

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Quote Details
            </h1>
            <p className="text-gray-600">Quote ID: {quote.quote_id}</p>
          </div>
          <div className="text-right">
            <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
              isValid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {isValid ? 'Valid' : 'Expired'}
            </div>
            <p className="text-gray-600 mt-1">
              Valid until: {formatDate(validUntil)}
            </p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Base Package */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Base Package</h2>
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-lg">{quote.base_package.name}</h3>
              <p className="text-gray-600 mb-2">{quote.base_package.description}</p>
              <div className="text-right">
                <span className="text-2xl font-bold text-blue-600">
                  {formatCurrency(quote.pricing.base_package_monthly)}/mo
                </span>
                {quote.pricing.base_package_setup && quote.pricing.base_package_setup > 0 && (
                  <p className="text-sm text-gray-500">
                    + {formatCurrency(quote.pricing.base_package_setup)} setup fee
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Toolkit Add-ons */}
          {quote.toolkit_addons.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Toolkit Add-ons</h2>
              <div className="space-y-3">
                {quote.toolkit_addons.map((toolkit, index) => (
                  <div key={index} className="flex justify-between items-center border-b pb-3">
                    <div>
                      <h3 className="font-medium">{toolkit.name}</h3>
                      <p className="text-sm text-gray-600">{toolkit.description}</p>
                      <span className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                        {toolkit.category}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="font-semibold">
                        {formatCurrency(toolkit.monthly_price || 0)}/mo
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Custom Services */}
          {quote.custom_services.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Custom Services</h2>
              <div className="space-y-3">
                {quote.custom_services.map((service, index) => (
                  <div key={index} className="flex justify-between items-center border-b pb-3">
                    <div>
                      <h3 className="font-medium">{service.name}</h3>
                      <p className="text-sm text-gray-600">{service.description}</p>
                    </div>
                    <div className="text-right">
                      <span className="font-semibold">
                        {formatCurrency(service.monthly_price || 0)}/mo
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Physical Products */}
          {quote.physical_products.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Physical Products</h2>
              <div className="space-y-3">
                {quote.physical_products.map((product, index) => (
                  <div key={index} className="flex justify-between items-center border-b pb-3">
                    <div>
                      <h3 className="font-medium">{product.name}</h3>
                      <p className="text-sm text-gray-600">{product.description}</p>
                      <span className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                        {product.category}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="font-semibold">
                        {formatCurrency(product.price || 0)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar - Pricing Summary */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
            <h2 className="text-xl font-semibold mb-4">Pricing Summary</h2>

            <div className="space-y-3 mb-4">
              <div className="flex justify-between">
                <span>Monthly Recurring</span>
                <span className="font-semibold">
                  {formatCurrency(quote.pricing.total_monthly)}
                </span>
              </div>

              {quote.pricing.total_setup > 0 && (
                <div className="flex justify-between">
                  <span>Setup Fees</span>
                  <span className="font-semibold">
                    {formatCurrency(quote.pricing.total_setup)}
                  </span>
                </div>
              )}

              {quote.pricing.total_one_time > 0 && (
                <div className="flex justify-between">
                  <span>One-time Items</span>
                  <span className="font-semibold">
                    {formatCurrency(quote.pricing.total_one_time)}
                  </span>
                </div>
              )}

              {quote.pricing.shipping_total > 0 && (
                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span className="font-semibold">
                    {formatCurrency(quote.pricing.shipping_total)}
                  </span>
                </div>
              )}
            </div>

            <div className="border-t pt-3 mb-6">
              <div className="flex justify-between text-lg font-bold">
                <span>Total Initial Payment</span>
                <span>
                  {formatCurrency(quote.pricing.total_setup + quote.pricing.total_one_time)}
                </span>
              </div>
              <div className="flex justify-between text-lg font-bold text-blue-600">
                <span>Monthly Billing</span>
                <span>{formatCurrency(quote.pricing.total_monthly)}</span>
              </div>
            </div>

            {isValid ? (
              <button
                onClick={handleConvertToOrder}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 font-semibold"
              >
                Accept Quote & Place Order
              </button>
            ) : (
              <div className="text-center">
                <p className="text-red-600 mb-3">This quote has expired</p>
                <Link
                  to="/packages"
                  className="inline-block bg-gray-600 text-white py-3 px-4 rounded-lg hover:bg-gray-700 font-semibold"
                >
                  Create New Quote
                </Link>
              </div>
            )}

            <div className="mt-4 text-sm text-gray-600">
              <p><strong>Estimated Delivery:</strong> {quote.estimated_deployment_time}</p>
              <p><strong>Support Level:</strong> {quote.support_level}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};